<template>
  <v-skeleton-loader
    :loading="isLoading"
    transition="expand-transition"
    type="article"
  >
    <FormProcessCheckSheet
      :ProcessCheckSheet="editProcessCheckSheet"
      :stockItem="stockItem"
      :isLoading="isSaving"
      @save="save"
      @update="update"
      @cancel="$router.push({name: from.name })" />
  </v-skeleton-loader>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import loading from '@codehq/aurora-app-core/src/mixins/loading';
import FormProcessCheckSheet from '../components/FormProcessCheckSheet.vue';
import stockItem from './stockItem';

export default {
  components: {
    FormProcessCheckSheet,
  },
  name: 'EditProcessCheckSheet',
  module: 'ProcessCheckSheet',
  mixins: [loading, stockItem],
  data() {
    return {
      /**
      * Get/Set editing ProcessCheckSheet. Default to {}.
      */
      editProcessCheckSheet: {
        saltBins: [{}],
        drainedWeights: [{}],
      },
      /**
       * Query Id for ProcessCheckSheet
       */
      processCheckSheetId: this.$route?.query.id,
    };
  },
  computed: {
    /** Vuex states
     */
    ...mapState('processCheckSheets', ['processCheckSheets', 'processCheckSheet']),
    /** Get the mode for this view, create or update
     */
    mode() {
      if (this.editProcessCheckSheet.id) {
        return 'edit';
      }
      return 'add';
    },
  },
  destroyed() {
    this.$off('stockItem.loaded');
  },
  async created() {
    this.$on('stockItem.loaded', async (item) => {
      this.isLoading = true;
      if (item.processCheckSheets?.length > 0) {
        // eslint-disable-next-line prefer-destructuring
        this.editProcessCheckSheet = item.processCheckSheets[0];
      } else {
        this.editProcessCheckSheet.stockItemId = item.id;
        this.editProcessCheckSheet.deliveryDate = this.formatDate(item.deliveryNote.receivingDate);
        this.editProcessCheckSheet.supplier = item.deliveryNote.farm.name;
        this.editProcessCheckSheet.farmGradeRange = `${item.sizeMin} - ${item.sizeMax}`;
        this.editProcessCheckSheet.supplierComments = item.deliveryNote.supplierNotes;
        this.editProcessCheckSheet.deliveryWeight = item.receivedMass.toFixed(3);
        this.editProcessCheckSheet.processingDatePouches = this.formatDate(item.productionDate);
        this.editProcessCheckSheet.processingDateCans = this.formatDate(item.productionDate);
        this.$log.info('Prepopulation completed');
        this.$forceUpdate();
      }
      this.isLoading = false;
    });
  },
  methods: {
    /** Vuex methods
     */
    ...mapActions('drainedWeights', ['CREATE_drainedWeight', 'UPDATE_drainedWeight']),
    ...mapActions('processCheckSheets', ['LOAD_processCheckSheet', 'CREATE_processCheckSheet', 'UPDATE_processCheckSheet', 'LOAD_processCheckSheetByStockItem']),
    ...mapActions('saltBins', ['CREATE_saltBin', 'UPDATE_saltBin']),
    ...mapActions('stockItems', ['SPLIT_stockItems', 'UPDATE_stockItem']),
    async update(item) {
      await this.save(item, true);
    },
    /** Method used to save the item
     */
    async save(processCheckSheet, skipRedirect) {
      this.isSaving = true;

      const pouches = parseFloat(processCheckSheet.meatMassBeforeProcessingPouches ?? '0');
      const canned = parseFloat(processCheckSheet.meatMassBeforeProcessingCans ?? '0');

      if (this.mode === 'add') {
        await this.CREATE_processCheckSheet(processCheckSheet);
      } else {
        const updatedProcessCheckSheet = { ...processCheckSheet };
        const { saltBins, drainedWeights } = processCheckSheet;
        if (this.stockItem.stockType === 'Wet' && processCheckSheet.status === 'Canning/Pouches') {
          const s = { ...this.stockItem };
          s.batchPrintingVerifications = undefined;
          s.deliveryNote = undefined;
          s.processCheckSheets = undefined;
          s.canEndInspections = undefined;
          s.canningProcesses = undefined;
          s.brand = undefined;
          s.processingMass = pouches + canned;
          await this.UPDATE_stockItem(s);
          updatedProcessCheckSheet.status = 'Ready to be verified';
        }

        // update the existing process checksheet
        updatedProcessCheckSheet.saltBins = undefined;
        updatedProcessCheckSheet.drainedWeights = undefined;
        await this.UPDATE_processCheckSheet(updatedProcessCheckSheet);
        saltBins.forEach((b) => {
          if (b.id) {
            this.UPDATE_saltBin(b);
          } else {
            const saltBin = { ...b };
            saltBin.processCheckSheetId = this.editProcessCheckSheet.id;
            this.CREATE_saltBin(saltBin);
          }
        });
        drainedWeights.forEach((b) => {
          if (b.id) {
            this.UPDATE_drainedWeight(b);
          } else {
            const drainedWeight = { ...b };
            drainedWeight.processCheckSheetId = this.editProcessCheckSheet.id;
            this.CREATE_drainedWeight(drainedWeight);
          }
        });
      }

      // check if split stock item if it is of stock type Wet

      if (this.stockItem.stockType === 'Wet' && processCheckSheet.status === 'Canning/Pouches') {
        const { totalWetMeatAndBloodWeight } = processCheckSheet;
        const total = pouches + canned;
        const newItems = [];
        console.debug({ pouches, canned });
        if (pouches > 0) {
          const pouchesPercentage = (pouches / total) * 100;
          const item = {
            stockType: 'Pouches',
            sizeMin: this.stockItem.sizeMin,
            sizeMax: this.stockItem.sizeMax,
            deliveryNoteId: this.stockItem.deliveryNoteId,
            receivedMass: this.stockItem.receivedMass * (pouchesPercentage / 100),
            packedMass: this.stockItem.packedMass * (pouchesPercentage / 100),
            harvestedMass: this.stockItem.harvestedMass * (pouchesPercentage / 100),
            processingMass: pouches,
            quantity: pouches,
            splitPercentage: pouchesPercentage,
            status: 'Canning/Pouches',
            totalWetMeatAndBloodWeight: totalWetMeatAndBloodWeight * (pouchesPercentage / 100),
          };
          newItems.push(item);
        }
        if (canned > 0) {
          const cannedPercentage = (canned / total) * 100;
          const item = {
            stockType: 'Canned',
            sizeMin: this.stockItem.sizeMin,
            sizeMax: this.stockItem.sizeMax,
            deliveryNoteId: this.stockItem.deliveryNoteId,
            receivedMass: this.stockItem.receivedMass * (cannedPercentage / 100),
            packedMass: this.stockItem.packedMass * (cannedPercentage / 100),
            harvestedMass: this.stockItem.harvestedMass * (cannedPercentage / 100),
            processingMass: canned,
            quantity: canned,
            splitPercentage: cannedPercentage,
            status: 'Canning/Pouches',
            totalWetMeatAndBloodWeight: totalWetMeatAndBloodWeight * (cannedPercentage / 100),
          };
          newItems.push(item);
        }
        if (newItems.length > 0) {
          const data = {
            stockItem: {
              id: this.stockItem.id,
            },
            stockItems: newItems,
          };
          await this.SPLIT_stockItems(data);
          console.debug(data);
        }
        console.debug({ pouches, canned, totalWetMeatAndBloodWeight });
      }
      this.isSaving = false;
      if (!skipRedirect) {
        this.$router.push({ name: 'processing-ListFactoryItem' });
      }
    },
  },
};
</script>

<docs>
# EditProcessCheckSheet example

'''js
<EditProcessCheckSheet :ProcessCheckSheet="ProcessCheckSheet" />
'''
</docs>
